import type { FileRef } from '@reikraft/shared';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly fields: Record<string, string> = {},
    readonly code?: string,
  ) {
    super(message);
  }
}

type Body = unknown;

async function request<T>(method: string, path: string, body?: Body): Promise<T> {
  const isForm = body instanceof FormData;
  const res = await fetch(`/api${path}`, {
    method,
    credentials: 'same-origin',
    headers: body !== undefined && !isForm ? { 'content-type': 'application/json' } : undefined,
    body: body === undefined ? undefined : isForm ? body : JSON.stringify(body),
  });
  const text = await res.text();
  let data: unknown;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = { message: text };
  }
  if (!res.ok) {
    const d = (data ?? {}) as { message?: string; fields?: Record<string, string>; code?: string };
    if (res.status === 401 && !path.startsWith('/admin/auth/')) {
      const next = encodeURIComponent(location.pathname + location.search);
      if (!location.pathname.startsWith('/login')) location.assign(`/login?next=${next}`);
    }
    throw new ApiError(d.message ?? `Ошибка ${res.status}`, res.status, d.fields ?? {}, d.code);
  }
  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: Body) => request<T>('POST', path, body ?? {}),
  put: <T>(path: string, body?: Body) => request<T>('PUT', path, body ?? {}),
  patch: <T>(path: string, body?: Body) => request<T>('PATCH', path, body ?? {}),
  del: <T>(path: string) => request<T>('DELETE', path),
};

export async function uploadFiles(files: FileList | File[]): Promise<FileRef[]> {
  const out: FileRef[] = [];
  for (const f of Array.from(files)) {
    const fd = new FormData();
    fd.append('file', f);
    out.push(await request<FileRef>('POST', '/admin/files', fd));
  }
  return out;
}

export function errorText(e: unknown): string {
  return e instanceof Error ? e.message : 'Что-то пошло не так';
}

/** Строка запроса без пустых значений */
export function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
}
